import React from 'react';
import type { NextVisitData } from '../../../../types/contactLog';
import { formatISODateToJapanese } from '../../../../utils';
import { DateRangeIcon } from '../../../../components/Icons';
import {
  Section,
  Title,
  Card,
  Row,
  LabelGroup,
  IconWrapper,
  Label,
  DateValue,
  DateNumber,
  DateUnit,
  Note,
} from './NextVisitSection.styled';

interface NextVisitSectionProps {
  /**
   * APIレスポンスの next_visit。
   * null / undefined は「表示しない」設定（またはAPI未対応）を意味し、セクションごと非表示にする
   */
  nextVisit?: NextVisitData | null;
  /**
   * 表示中の連絡帳の日付（YYYY-MM-DD）。
   * 次回来所日はこの日付時点の情報であることを補足文言で示すために使う。
   * 取得できない場合は日付なしの汎用文言にフォールバックする
   */
  contactDate?: string;
}

const NextVisitSectionComponent: React.FC<NextVisitSectionProps> = ({ nextVisit, contactDate }) => {
  // 事業所単位・利用者/実施日単位のいずれかが「表示しない」の場合、APIはnullを返す。
  // APIが未対応でundefinedの場合も、非表示側に倒して日付が漏れないようにする。
  if (!nextVisit) {
    return null;
  }

  // 連絡帳レコードが無い、次回来所日が未入力、日付が不正のいずれでも空文字になる。
  // その場合はタイトルごとトルツメする。
  const dateText = formatISODateToJapanese(nextVisit.date);
  if (dateText === '') {
    return null;
  }

  // 数字とそれ以外（年月日・曜日）を別要素に分け、数字だけ大きく表示する
  const dateParts = dateText.split(/(\d+)/).filter((part) => part !== '');

  // 連絡帳の日付が取得できない場合は、誤った時点を示さないよう日付なしの汎用文言に切り替える
  const contactDateText = formatISODateToJapanese(contactDate);
  const noteText =
    contactDateText !== ''
      ? `※${contactDateText} 配信時点`
      : '※この画面では日程の更新はされません。';

  return (
    <Section aria-labelledby="next-visit-title">
      <Title id="next-visit-title">来所日について</Title>
      <Card>
        <Row>
          <LabelGroup>
            <IconWrapper aria-hidden="true">
              <DateRangeIcon />
            </IconWrapper>
            <Label>次回来所日</Label>
          </LabelGroup>
          <DateValue>
            {dateParts.map((part, index) =>
              /^\d+$/.test(part) ? (
                <DateNumber key={index}>{part}</DateNumber>
              ) : (
                <DateUnit key={index}>{part}</DateUnit>
              )
            )}
          </DateValue>
        </Row>
        <Note>{noteText}</Note>
      </Card>
    </Section>
  );
};

export const NextVisitSection = React.memo(NextVisitSectionComponent);

export default NextVisitSection;
